import Cookies from 'js-cookie';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector, } from 'react-redux';
import api from '../services/api';
import { resetLogout, signInSuccess } from '../store/modules/auth/actions';
import { User } from '../store/modules/auth/types';
import { useHistory, useLocation } from 'react-router-dom';
import { getCheckout } from '../store/modules/checkout/actions';
import { routesEndpoints } from '../routes/endpoint';
import { StoreState } from '../store/createStore';

interface AuthState {
  user: User;
}

interface SingInCredentias {
  name?: string;
  email: string;
  password: string;
}

interface AuthContextData {
  user: User;
  signIn(credentials: SingInCredentias): Promise<void>;
  signUp(credentials: SingInCredentias): Promise<void>;
  signOut(): void;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextData | null>(null);

const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
}: AuthProviderProps) => {
  const history = useHistory();
  const location = useLocation();
  const USER_KEY = '@ConectoVarejo:user';

  const dispatch = useDispatch();
  const { user, isLogout } = useSelector((state: StoreState) => state.auth);
  const [data, setData] = useState<AuthState>(() => {
    const userCookie = Cookies.get(USER_KEY);
    // Verifica se o userCookie existe e não está vazio
    if (userCookie) {
      try {
        // Tenta fazer o parse do cookie, mas se falhar, retorna um valor padrão
        const user = JSON.parse(userCookie);
        return { user };
      } catch (error) {
        console.error('Erro ao analisar o cookie do usuário:', error);
        return {} as AuthState; // Retorna um valor padrão em caso de erro
      }
    } else {
      return {} as AuthState;
    }
  });

  useEffect(() => {
    if (isLogout) {
      dispatch(resetLogout());
      history.push(routesEndpoints.HOME);
    }
  }, [isLogout, dispatch, history]);

  useEffect(() => {
    if (data.user?.data && user.data?.id && data?.user?.data.id !== user.data.id) {
      setData({
        user,
      })
    }
  }, [user, data]);

  const signIn = useCallback(async ({ email, password }) => {
    try {
      const response = await api.post('auth/session', { email, password });
      const { user } = response.data;
      Cookies.set(USER_KEY, JSON.stringify(user), { expires: 1 }); // Cookie expira em 7 dias

      setData({ user });
    } catch (error) {
      console.error('Sign-in failed:', error);

      throw new Error('Unable to sign in. Please check your credentials.');
    }
  }, []);

  const signUp = useCallback(async ({ name, email, password }) => {
    const response = await api.post('auth/create', { name, email, password });

    const { user } = response.data;

    if (user) {
      Cookies.set(USER_KEY, JSON.stringify(user), { expires: 1 });

      setData({ user });
    } else {
      setData({} as AuthState);
    }
  }, []);

  const signOut = useCallback(() => {
    Cookies.remove(USER_KEY);

    setData({} as AuthState);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (data.user) {
        const agreement = true;
        if (location.pathname !== routesEndpoints.NEW_CHECKOUT) {
          dispatch(getCheckout());
        }
        dispatch(signInSuccess({ user: data.user, agreement }));
      }
    };

    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.user, dispatch]);

  return (
    <AuthContext.Provider value={{ user: data?.user, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
